import { Navigate, Route } from 'react-router-dom';
import { lazyRoute } from './lib/lazyRoute';

// The app shell — layouts, route guards, and the login page — is loaded eagerly
// so the initial render path is fast. Everything else is lazy-loaded below.
import { AuthenticatedLayout } from './components/layout/AuthenticatedLayout';
import { PublicLayout } from './components/layout/PublicLayout';
import { AdminRoute } from './components/routing/AdminRoute';
import { ProtectedRoute } from './components/routing/ProtectedRoute';
import { PublicOnlyRoute } from './components/routing/PublicOnlyRoute';
import { LegacyPatientPathRedirect } from './components/routing/LegacyPatientPathRedirect';
import { PatientSessionScope } from './components/routing/PatientSessionScope';
import { SessionScope } from './components/routing/SessionScope';
import Login from './pages/Login';

// All other pages are lazy-loaded so the initial bundle stays small: the login
// path no longer pulls the entire authenticated app (admin pages, the PDF
// viewer, Stripe, Maps, etc.) into the first download. Each page becomes its own
// chunk fetched on navigation, with <PageLoader> shown via Suspense meanwhile.
// lazyRoute (not bare React.lazy — ESLint-enforced) also recovers from
// post-deploy stale-chunk failures with a one-shot reload.
const AcceptInvitePage = lazyRoute(() => import('./pages/AcceptInvitePage'));
const SchedulingFollowUpPage = lazyRoute(
  () => import('./pages/SchedulingFollowUpPage')
);
const VisitFollowUpPage = lazyRoute(() => import('./pages/VisitFollowUpPage'));
const SignAuthorizationPage = lazyRoute(
  () => import('./pages/SignAuthorizationPage')
);
const AcceptInvitesPage = lazyRoute(
  () =>
    import('./pages/AcceptInvitesPage').then(m => ({
      default: m.AcceptInvitesPage,
    })),
  '/accept-invites'
);
const AddReferralPage = lazyRoute(() => import('./pages/AddReferralPage'));
const AdminPatientProfilePage = lazyRoute(
  () => import('./pages/AdminPatientProfilePage')
);
const AdminRecordsRequestDetailPage = lazyRoute(
  () => import('./pages/AdminRecordsRequestDetailPage')
);
const AdminReferralDetailPage = lazyRoute(
  () => import('./pages/AdminReferralDetailPage')
);
const AdminPatientSearchPage = lazyRoute(
  () => import('./pages/AdminPatientSearchPage'),
  '/admin/patients'
);
const ClinicsListPage = lazyRoute(
  () => import('./pages/ClinicsListPage'),
  '/admin/clinics'
);
const ClinicDetailPage = lazyRoute(() => import('./pages/ClinicDetailPage'));
const AllReferralsPage = lazyRoute(
  () => import('./pages/AllReferralsPage'),
  '/all-referrals'
);
const AppointmentDetailPage = lazyRoute(
  () => import('./pages/AppointmentDetailPage')
);
const AppointmentsPage = lazyRoute(() => import('./pages/AppointmentsPage'));
const BaaPage = lazyRoute(() => import('./pages/BaaPage'));
const BookingPage = lazyRoute(() => import('./pages/BookingPage'));
const CallManagementPage = lazyRoute(() =>
  import('./pages/CallManagementPage').then(m => ({
    default: m.CallManagementPage,
  }))
);
const NotificationsPage = lazyRoute(() =>
  import('./pages/NotificationsPage').then(m => ({
    default: m.NotificationsPage,
  }))
);
const EditReferralPage = lazyRoute(() => import('./pages/EditReferralPage'));
const EmailVerificationPage = lazyRoute(
  () => import('./pages/EmailVerificationPage')
);
const FaxesSentPage = lazyRoute(
  () => import('./pages/FaxesSentPage'),
  '/faxes-sent'
);
const ForgotPasswordPage = lazyRoute(
  () => import('./pages/ForgotPasswordPage')
);
const MyProfile = lazyRoute(() => import('./pages/MyProfile'), '/my-profile');
const InboundReferralDetailPage = lazyRoute(
  () => import('./pages/InboundReferralDetailPage')
);
const OutboundReferralDetailPage = lazyRoute(
  () => import('./pages/OutboundReferralDetailPage')
);
const MrrOnboardingPage = lazyRoute(() => import('./pages/MrrOnboardingPage'));
const ProviderOnboardingPage = lazyRoute(
  () => import('./pages/ProviderOnboardingPage')
);
const RecordsRequestDetailPage = lazyRoute(
  () => import('./pages/RecordsRequestDetailPage')
);
const RecordsRequestSendPage = lazyRoute(
  () => import('./pages/RecordsRequestSendPage')
);
const RecordsRequestsPage = lazyRoute(
  () => import('./pages/RecordsRequestsPage'),
  '/records-requests'
);
const ReferralsPage = lazyRoute(
  () => import('./pages/ReferralsPage'),
  '/referrals'
);
const RegisterPage = lazyRoute(() => import('./pages/RegisterPage'));
const ResetPasswordPage = lazyRoute(() => import('./pages/ResetPasswordPage'));
const SendPage = lazyRoute(() => import('./pages/SendPage'), '/faxes/send');
const SettingsPage = lazyRoute(
  () => import('./pages/SettingsPage'),
  '/settings'
);
const StaffPage = lazyRoute(() => import('./pages/StaffPage'), '/staff');
const PatientDetailPage = lazyRoute(() => import('./pages/PatientDetailPage'));
const PatientPortalProfilePage = lazyRoute(
  () => import('./pages/PatientPortalProfilePage')
);
const PatientPortalRequestPage = lazyRoute(
  () => import('./pages/PatientPortalRequestPage')
);
const PatientPortalRequestsPage = lazyRoute(
  () => import('./pages/PatientPortalRequestsPage'),
  '/profile'
);
const PatientPortalSignInPage = lazyRoute(
  () => import('./pages/PatientPortalSignInPage'),
  '/profile/sign-in'
);
const PatientsPage = lazyRoute(
  () => import('./pages/PatientsPage'),
  '/patients'
);
const PendingJoinPage = lazyRoute(() => import('./pages/PendingJoinPage'));
const FaxesPage = lazyRoute(() => import('./pages/FaxesPage'), '/admin/faxes');
const ViewFaxPage = lazyRoute(() => import('./pages/ViewFaxPage'));

// Kept out of App.tsx so tests can match paths against the tree, or render
// one route through it, without the app shell's Sentry and analytics bootstrap.
export function appRoutes() {
  return (
    <>
      {/* Everything that can have a staff session. */}
      <Route element={<SessionScope />}>
        {/* Public routes */}
        <Route element={<PublicOnlyRoute />}>
          <Route
            path="/provider-onboarding"
            element={<ProviderOnboardingPage />}
          />
          <Route
            path="/records-request-onboarding"
            element={<MrrOnboardingPage />}
          />
          <Route element={<PublicLayout />}>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
          </Route>
        </Route>

        {/* Protected routes - nest MainLayout inside ProtectedRoute */}
        <Route element={<ProtectedRoute />}>
          {/* Email verification page - outside AuthenticatedLayout */}
          <Route path="/verify-email" element={<EmailVerificationPage />} />

          {/* Pending join-or-create page for matched signups (Project A)
              - outside AuthenticatedLayout, like /verify-email */}
          <Route path="/pending" element={<PendingJoinPage />} />

          <Route element={<AuthenticatedLayout />}>
            {/* Admin-only routes */}
            <Route element={<AdminRoute />}>
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/all-referrals" element={<AllReferralsPage />} />
              <Route path="/calls" element={<CallManagementPage />} />
              <Route path="/appointments" element={<AppointmentsPage />} />
              <Route
                path="/appointments/:id"
                element={<AppointmentDetailPage />}
              />
              <Route
                path="/admin/patients"
                element={<AdminPatientSearchPage />}
              />
              <Route path="/admin/clinics" element={<ClinicsListPage />} />
              <Route
                path="/admin/clinics/:clinicId"
                element={<ClinicDetailPage />}
              />
              <Route path="/admin/faxes" element={<FaxesPage />} />
              {/* Back-compat redirect for the former Incoming Faxes URL */}
              <Route
                path="/incoming-faxes"
                element={<Navigate to="/admin/faxes" replace />}
              />
            </Route>
            {/* Regular protected routes */}
            <Route
              path="/home"
              element={<Navigate to="/referrals" replace />}
            />
            <Route path="/referrals" element={<ReferralsPage />} />
            <Route path="/referrals/:id" element={<ReferralsPage />} />
            <Route path="/patients" element={<PatientsPage />} />
            <Route path="/patients/:id" element={<PatientDetailPage />} />
            <Route path="/records-requests" element={<RecordsRequestsPage />} />
            <Route
              path="/records-requests/new"
              element={<RecordsRequestSendPage />}
            />
            <Route
              path="/records-requests/:id/view"
              element={<RecordsRequestDetailPage />}
            />
            <Route path="/faxes-sent" element={<FaxesSentPage />} />
            <Route path="/staff" element={<StaffPage />} />
            <Route path="/my-profile" element={<MyProfile />} />
            <Route path="/accept-invites" element={<AcceptInvitesPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route
              path="/referrals/:id/view"
              element={<OutboundReferralDetailPage />}
            />
            <Route
              path="/referrals/:id/inbound"
              element={<InboundReferralDetailPage />}
            />
            <Route path="/faxes/send" element={<SendPage />} />
            <Route path="/" element={<Navigate to="/referrals" replace />} />
          </Route>

          {/* Admin-only routes outside AuthenticatedLayout */}
          <Route element={<AdminRoute />}>
            <Route
              path="/all-referrals/:id"
              element={<AdminReferralDetailPage />}
            />
            <Route
              path="/all-referrals/:id/edit"
              element={<EditReferralPage />}
            />
            <Route
              path="/admin/records-requests/:id"
              element={<AdminRecordsRequestDetailPage />}
            />
            <Route
              path="/admin/patients/:id"
              element={<AdminPatientProfilePage />}
            />
          </Route>
          {/* Regular protected routes outside AuthenticatedLayout */}
          <Route path="/referrals/new" element={<AddReferralPage />} />
          <Route path="/referrals/:id/edit" element={<EditReferralPage />} />
          <Route path="/faxes/:id" element={<ViewFaxPage />} />
        </Route>

        {/* Mixed access: signs an invitee in, or branches on the
            current user. */}
        <Route element={<PublicLayout />}>
          <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
        </Route>
      </Route>

      {/* Patient booking wizard, embedded as an iframe on the
          marketing site. Bare: PublicLayout is chrome the iframe must
          not carry, and PublicOnlyRoute would bounce logged-in staff
          out of a page that is public to everyone. */}
      <Route path="/book" element={<BookingPage />} />

      {/* Patient link pages: no session, no /auth/me. */}
      <Route element={<PublicLayout />}>
        <Route path="/followup/:token" element={<SchedulingFollowUpPage />} />
        <Route path="/visit-followup/:token" element={<VisitFollowUpPage />} />
        <Route path="/sign/:token" element={<SignAuthorizationPage />} />
        <Route path="/baa" element={<BaaPage />} />
      </Route>

      {/* Patient account pages, top level: WordPress links here. Their own
          session provider, so no staff route is ever called. */}
      <Route element={<PatientSessionScope />}>
        <Route path="/profile" element={<PatientPortalRequestsPage />} />
        <Route
          path="/profile/requests/:id"
          element={<PatientPortalRequestPage />}
        />
        <Route path="/profile/details" element={<PatientPortalProfilePage />} />
      </Route>
      <Route path="/profile/sign-in" element={<PatientPortalSignInPage />} />
      {/* The paths before the rename, still in emails already sent. */}
      <Route path="/your-health/*" element={<LegacyPatientPathRedirect />} />

      {/* 404 catch-all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </>
  );
}
